/**
 * Discover the structured tool call associated with an assistant message.
 *
 * Used by both the chat-bottom download button and the Monaco code-block
 * download button. Both need to find the SOQL query (or other structured
 * MCP tool call) that produced the data the user wants to export.
 *
 * Source order — the FIRST hit wins:
 *   1. The message's own `toolCalls` (passed in as a prop) — strict per-message
 *
 * NOTE:
 *   - we do NOT walk the global feed store
 *   - we do NOT guess from free-form message content anymore
 * Per-message tool metadata only. That removed the old wrong-message and
 * wrong-query correlation bugs.
 */
const STRUCTURED_TOOLS = new Set<string>([
  "run_soql_query",
  "tooling_query",
  "list_metadata",
  "get_object_fields",
  "get_metadata_by_name",
  "graph_dependents",
  "graph_calls",
  "graph_impact",
  "graph_data_access",
  "graph_object_usage",
  "graph_triggers_on",
  "semantic_search",
  "find_references",
  "get_quality_report",
  "query_security_findings",
]);

export interface ToolCallHit {
  tool: string;
  args: Record<string, unknown>;
}

const SOQL_TOOLS = new Set(["run_soql_query", "tooling_query"]);

/** True if the tool name produces structured rows we can render. */
export const isStructuredTool = (tool: string | undefined | null): boolean =>
  typeof tool === "string" && STRUCTURED_TOOLS.has(tool);

/** True if the tool is a SOQL/Tooling query (uses /download-soql, not /download-tool-result). */
export const isSoqlTool = (tool: string | undefined | null): boolean =>
  typeof tool === "string" && SOQL_TOOLS.has(tool);

const findInToolCallsArray = (tcs: unknown): ToolCallHit | null => {
  if (!Array.isArray(tcs)) return null;
  // Two-pass walk in REVERSE.
  //
  // Pass 1: prefer SOQL/tooling queries — they're the canonical "give me rows"
  //   tools and the LAST one is usually the refined query the user cares about.
  // Pass 2: fall back to any structured tool (list_metadata, graph_*, etc.)
  //
  // Why two passes: when the agent runs e.g. [list_metadata, run_soql_query,
  // list_metadata], a single-pass "last match wins" picks the trailing
  // list_metadata which may have different args than the SOQL that actually
  // produced the rows the user saw. Preferring SOQL tools fixes that.
  const extract = (rec: Record<string, unknown>): ToolCallHit | null => {
    const tool = (rec.tool || rec.name) as string | undefined;
    const args = (rec.args || rec.arguments || {}) as Record<string, unknown>;
    if (isSoqlTool(tool) && typeof args?.query !== "string") {
      return null;
    }
    return tool ? { tool, args } : null;
  };
  for (let i = tcs.length - 1; i >= 0; i--) {
    const tc = tcs[i];
    if (!tc || typeof tc !== "object") continue;
    const rec = tc as Record<string, unknown>;
    const tool = (rec.tool || rec.name) as string | undefined;
    if (isSoqlTool(tool)) {
      const hit = extract(rec);
      if (hit) return hit;
    }
  }
  for (let i = tcs.length - 1; i >= 0; i--) {
    const tc = tcs[i];
    if (!tc || typeof tc !== "object") continue;
    const rec = tc as Record<string, unknown>;
    const tool = (rec.tool || rec.name) as string | undefined;
    if (isStructuredTool(tool)) {
      const hit = extract(rec);
      if (hit) return hit;
    }
  }
  return null;
};

export const findStructuredToolCall = (
  toolCalls: unknown[] | undefined,
  _content: string,
): ToolCallHit | null => {
  return findInToolCallsArray(toolCalls);
};
